import React, {useLayoutEffect, useRef, useState} from 'react';

// Matches the .phq-collapse transition in index.css.
export const COMPOSER_BANNER_EXIT_MS = 220;

interface ComposerBannerProps {
    open: boolean;
    /** Render the banner only while it is open (`{x && <div/>}`); the last content is kept while it collapses. */
    children: React.ReactNode;
}

/**
 * Expands and collapses a banner above the composer (reply, edit, notices) to its real height.
 * On close it keeps the last content on screen until the collapse finishes, so it doesn't snap away.
 */
export function ComposerBanner({open, children}: ComposerBannerProps) {
    const [mounted, setMounted] = useState(open);
    const [expanded, setExpanded] = useState(false);
    const rootRef = useRef<HTMLDivElement>(null);
    const lastChildren = useRef<React.ReactNode>(children);
    if (open) lastChildren.current = children;

    useLayoutEffect(() => {
        if (open) {
            setMounted(true);
            // Flush the collapsed style first, otherwise the browser skips the transition.
            void rootRef.current?.offsetHeight;
            setExpanded(true);
            return;
        }
        setExpanded(false);
        const timer = window.setTimeout(() => setMounted(false), COMPOSER_BANNER_EXIT_MS);
        return () => window.clearTimeout(timer);
    }, [open]);

    if (!open && !mounted) return null;

    return (
        <div ref={rootRef} className="phq-collapse" data-expanded={expanded} aria-hidden={open ? undefined : true}>
            <div className="phq-collapse-inner">{open ? children : lastChildren.current}</div>
        </div>
    );
}
